/* eslint-disable @typescript-eslint/no-unnecessary-type-parameters */
// oxlint-disable typescript/no-explicit-any
import { isObject, toArray } from './object-guards.functions';

import type { DottedObjPaths, GetConfig, SetConfig } from '@kp/types';

type GetByDottedPath<
  T,
  Path extends string,
> = Path extends `${infer Key}.${infer Rest}`
  ? GetByDottedPath<PropertyValue<T, Key>, Rest>
  : PropertyValue<T, Path>;

type GetByKeyPath<T, Path> = Path extends readonly [
  infer Key extends PropertyKey,
  ...infer Rest,
]
  ? GetByKeyPath<PropertyValue<T, Key>, Rest>
  : T;

type ObjectPath = string | Array<string>;

type PropertyValue<
  T,
  Key extends PropertyKey,
> = Key extends keyof NonNullable<T>
  ? NonNullable<T>[Key]
  : Key extends `${infer Index extends number}`
    ? Index extends keyof NonNullable<T>
      ? NonNullable<T>[Index]
      : never
    : never;

type ReplaceEntry<
  Obj extends Record<string, any>,
  Key extends DottedObjPaths<Obj>,
> = [key: Key, mapFn: (value: GetByDottedPath<Obj, Key>) => unknown];

type ValidDottedPath<Obj, Path extends string> = [
  GetByDottedPath<Obj, Path>,
] extends [never]
  ? never
  : Path;

function appendValue(obj: Record<string, any>, path: string, value: any): void {
  const currentValue = obj[path];

  if (Array.isArray(currentValue)) {
    obj[path] = [...currentValue, value];

    return;
  }

  if (Object.hasOwn(obj, path)) {
    obj[path] = [currentValue, value];

    return;
  }

  obj[path] = [value];
}

function get<Obj extends object, const Path extends string>(
  obj: Obj,
  path: ValidDottedPath<Obj, Path>,
  config?: Partial<GetConfig>,
): GetByDottedPath<Obj, Path>;
function get<Obj extends object, const Path extends ReadonlyArray<string>>(
  obj: Obj,
  path: Path,
  config?: Omit<Partial<GetConfig>, 'multiple'>,
): GetByKeyPath<Obj, Path>;
function get<T, Undefined = true>(
  obj: object,
  path: ObjectPath,
  config?: Partial<GetConfig>,
): Undefined extends true ? T | undefined : T;
function get(
  obj: object | undefined,
  path: ObjectPath,
  config?: Partial<GetConfig>,
): unknown {
  if (!obj) {
    return undefined;
  }

  const { includes, multiple }: GetConfig = {
    includes: false,
    multiple: false,
    ...config,
  };

  return multiple
    ? toArray(path).map(currentPath => getSingle(obj, currentPath, includes))
    : getSingle(obj, path, includes);
}

function getReplaceEntries<
  Obj extends Record<string, any>,
  Key extends DottedObjPaths<Obj>,
>(
  values: Array<ReplaceEntry<Obj, Key>> | Key,
  mapFn?: (value: GetByDottedPath<Obj, Key>) => unknown,
): Array<ReplaceEntry<Obj, Key>> {
  if (Array.isArray(values)) {
    return values;
  }

  // mapFn is guaranteed by the single-path overload.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return [[values, mapFn]] as Array<ReplaceEntry<Obj, Key>>;
}

// oxlint-disable-next-line max-statements
function getSingle(
  obj: object,
  path: ObjectPath,
  includesConfig: boolean,
): unknown {
  const segments = Array.isArray(path) ? path : path.split('.');

  if (segments.length === 0) {
    return undefined;
  }

  let current: unknown = obj;

  for (let index = 0; index < segments.length; index += 1) {
    const key = segments[index];

    if (!key) {
      return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const item = (current as Record<string, unknown>)[key];
    const remainingSegments = segments.length - index - 1;

    if (!item || remainingSegments === 0) {
      return item;
    }

    if (includesConfig && remainingSegments === 1 && Array.isArray(item)) {
      return item.includes(segments[index + 1]);
    }

    current = item;
  }

  return current;
}

function isAttributePathValid(obj: object, path: string): boolean {
  let currentObject: unknown = obj;

  for (const segment of path.split('.')) {
    if (!isObject(currentObject) || !Object.hasOwn(currentObject, segment)) {
      return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    currentObject = (currentObject as Record<string, unknown>)[segment];

    if (currentObject === undefined) {
      return false;
    }
  }

  return true;
}

function nullifyEmptyObject<T extends object>(
  obj: T,
  paths: ObjectPath,
): Partial<T> {
  const pathsArray = toArray(paths);
  const retObj = structuredClone(obj);

  for (const path of pathsArray) {
    const value = get(obj, path);

    if (value === null || value === undefined) {
      setMut(retObj, path.split('.').slice(0, -1), null);
    }
  }

  return retObj;
}

// oxlint-disable-next-line max-statements
function objectToDottedPaths<T extends object>(
  obj: T | null | undefined,
  parentName = '',
): Array<DottedObjPaths<T>> {
  if (obj === null || obj === undefined) {
    return [];
  }

  const paths: Array<string> = [];
  const prefix = parentName ? `${parentName}.` : '';

  for (const [key, value] of Object.entries(obj)) {
    const keyName = `${prefix}${key}`;
    const entries =
      isObject(value) || Array.isArray(value) ? Object.entries(value) : [];

    if (entries.length === 0) {
      paths.push(keyName);

      continue;
    }

    paths.push(...objectToDottedPaths(value, keyName));
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return paths as Array<DottedObjPaths<T>>;
}

function replaceInObjectMut<
  Obj extends Record<string, any>,
  Key extends DottedObjPaths<Obj>,
>(object: Obj, values: Array<ReplaceEntry<Obj, Key>>): void;
function replaceInObjectMut<
  Obj extends Record<string, any>,
  Key extends DottedObjPaths<Obj>,
  Result,
>(
  object: Obj,
  path: Key,
  mapFn: (value: GetByDottedPath<Obj, Key>) => Result,
): void;
function replaceInObjectMut<
  Obj extends Record<string, any>,
  Key extends DottedObjPaths<Obj>,
>(
  object: Obj,
  values: Array<ReplaceEntry<Obj, Key>> | Key,
  mapFn?: (value: GetByDottedPath<Obj, Key>) => unknown,
): void {
  const entries = getReplaceEntries(values, mapFn);

  for (const [path, replaceFn] of entries) {
    if (!isAttributePathValid(object, path)) {
      continue;
    }

    const value = get<GetByDottedPath<Obj, Key>, false>(object, path);

    setMut(object, path, replaceFn(value));
  }
}

// oxlint-disable-next-line max-params
function set<T>(
  object: T,
  path: ObjectPath,
  value: any,
  config: Partial<SetConfig> = {},
): T {
  const obj = structuredClone(object);

  setMut(obj, path, value, config);

  return obj;
}

// oxlint-disable-next-line max-params
function setCurrentValue(
  obj: Record<string, any>,
  path: string,
  value: any,
  concat: SetConfig['concat'],
): void {
  if (!concat) {
    obj[path] = value;

    return;
  }

  if (concat === 'array') {
    appendValue(obj, path, value);

    return;
  }

  obj[path] = {
    ...obj[path],
    ...value,
  };
}

// oxlint-disable-next-line max-params
function setMut(
  obj: any,
  path: ObjectPath,
  value: any,
  config: Partial<SetConfig> = {},
): void {
  const { concat = false, delimiter = '.' } = config;
  const [currentPath, ...rest] = Array.isArray(path)
    ? path
    : path.split(delimiter);

  if (!currentPath) {
    return;
  }

  if (rest.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    setNestedValue(obj, currentPath, rest, value, config);

    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  setCurrentValue(obj, currentPath, value, concat);
}

// oxlint-disable-next-line max-params
function setNestedValue(
  obj: Record<string, any>,
  currentPath: string,
  rest: Array<string>,
  value: any,
  config: Partial<SetConfig>,
): void {
  obj[currentPath] ||= {};

  setMut(obj[currentPath], rest, value, config);
}

export type { GetByDottedPath, GetByKeyPath, ObjectPath, ReplaceEntry };

export {
  get,
  nullifyEmptyObject,
  objectToDottedPaths,
  replaceInObjectMut,
  set,
  setMut,
};
